// In-memory store for the master-password session — the unlocked
// OPFS vault that hosts `/system/config.enc` plus its decrypted
// app-config. Populated by the MasterSetupWizard (first-time) and
// the MasterUnlockScreen (returning users); cleared by the single
// inactivity lock that also drops the current vault's handle.
//
// No persistence middleware — everything here lives in memory only.
// The wasm worker owns the underlying keys; we hold only the opaque
// handle and the decrypted config.

import { create } from "zustand";
import type { AppConfig } from "../worker/api";

export interface MasterSession {
  /** Opaque worker handle for the unlocked OPFS vault that hosts the app-config. */
  masterHandle: number;
  /**
   * Decrypted app-config loaded from `/system/config.enc`, or `null`
   * if the OPFS vault has no config blob yet (transient state on a
   * fresh install between `createOpfsVault` and the first
   * `saveAppConfig`). Healthy unlocked vaults always have a non-null
   * `appConfig`.
   */
  appConfig: AppConfig | null;
  /** Unix ms timestamp when master was unlocked. */
  unlockedAt: number;
}

interface MasterSessionStore {
  master: MasterSession | null;
  setMaster: (m: MasterSession) => void;
  setAppConfig: (cfg: AppConfig) => void;
  clear: () => void;
}

export const useMasterSession = create<MasterSessionStore>((set) => ({
  master: null,
  setMaster: (m) => set({ master: m }),
  setAppConfig: (cfg) =>
    set((state) =>
      state.master ? { master: { ...state.master, appConfig: cfg } } : {},
    ),
  clear: () => set({ master: null }),
}));
